import {ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {FormArray, FormGroup} from '@angular/forms';
import {
  JaarrapportageEntity,
  ClientEntity,
  TrialBalanceItem,
  BokkzAlgDataService,
  BokkzJrDataService, Jaarrekening, Jaarrapportage, BokkzJrLogic
} from "@snelstart-ventures/fiscaal/domain-logic";
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {
  combineLatest,
  distinctUntilChanged,
  Observable,
  ReplaySubject,
  Subject,
  takeUntil
} from 'rxjs';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SpecTrialbalanceComponent} from '../spec-trialbalance/spec-trialbalance.component';
import {SpecFormattedexplanationComponent} from '../spec-formattedexplanation/spec-formattedexplanation.component';
import {CommonModule} from '@angular/common';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-spec-iva',
  templateUrl: './spec-iva.component.html',
  imports: [
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    SharedUiButtonModule,
    SpecTrialbalanceComponent,
    SpecFormattedexplanationComponent,
    CommonModule
  ],
  styleUrls: ['./spec-iva.component.scss']
})
export class SpecIvaComponent implements OnInit, OnChanges, OnDestroy {

  @Input() public id!: string;
  @Input() selectedTabObservable!: Observable<number>;
  private selectedTabSubject = new ReplaySubject<number>(1);
  showCustomControls = true;
  private destroy$ = new Subject<void>();
  public selectedTab = 0;
  public index = 0;
  public dialogVisible = false;
  public client = {} as ClientEntity;
  public jrEntity = {} as JaarrapportageEntity;
  public isRechtspersoon = false;

  public intangibleAssetsFg = {} as FormGroup;
  public costsIncorporationShareIssueFg = {} as FormGroup;
  public developmentCostsFg = {} as FormGroup;
  public patentsTrademarksOtherRightsFg = {} as FormGroup;
  public goodwillFg = {} as FormGroup;
  public prepaymentsIntangibleAssetsFg = {} as FormGroup;
  public intangibleAssetsOtherFg = {} as FormGroup;

  public intangibleAssetsDisclosureFg = {} as FormGroup;
  public developmentCostsDisclosureFg = {} as FormGroup;
  public patentsTrademarksOtherRightsDisclosureFg = {} as FormGroup;
  public goodwillDisclosureFg = {} as FormGroup;
  public prepaymentsIntangibleAssetsDisclosureFg = {} as FormGroup;
  public intangibleAssetsOtherDisclosureFg = {} as FormGroup;

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: "Ja"},
    {value: false, label: "Nee"},
  ];

  public showDialog(input: number) {
    this.index = input
    this.dialogVisible = true;
  }

  constructor(
    public algDataService: BokkzAlgDataService,
    public jrDataService: BokkzJrDataService,
    public jrLogicService: BokkzJrLogic,
    private cdRef: ChangeDetectorRef,
  ) {
    this.createFormGroups()
  }

  ngOnInit(): void {
    if (this.selectedTabObservable) {
      this.selectedTabObservable
        .pipe(takeUntil(this.destroy$))
        .subscribe(tab => {
          this.selectedTabSubject.next(tab);
        });
    }

    combineLatest([
      this.selectedTabSubject.pipe(distinctUntilChanged()),
      this.jrDataService.jaarrapportageEntity
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([tab, jrEntity]) => {
        this.selectedTab = tab;
        this.jrEntity = jrEntity;
        this.patchData(jrEntity);
      });

    this.jrDataService.isRechtspersoon.subscribe((value) => {
      this.isRechtspersoon = value;
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedTabObservable']) {
      this.selectedTabObservable = changes['selectedTabObservable'].currentValue;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  createFormGroups() {
    this.intangibleAssetsFg = this.jrDataService.createFormGroupMonetaryNoDecimalsPeriodValues()
    this.costsIncorporationShareIssueFg = this.jrDataService.createFormGroupBalanceItem();
    this.developmentCostsFg = this.jrDataService.createFormGroupBalanceItem();
    this.patentsTrademarksOtherRightsFg = this.jrDataService.createFormGroupBalanceItem();
    this.goodwillFg = this.jrDataService.createFormGroupBalanceItem();
    this.prepaymentsIntangibleAssetsFg = this.jrDataService.createFormGroupBalanceItem();
    this.intangibleAssetsOtherFg = this.jrDataService.createFormGroupBalanceItem();
    this.intangibleAssetsDisclosureFg = this.jrDataService.createFormGroupFormattedExplanation();
    this.developmentCostsDisclosureFg = this.jrDataService.createFormGroupFormattedExplanation();
    this.patentsTrademarksOtherRightsDisclosureFg = this.jrDataService.createFormGroupFormattedExplanation();
    this.goodwillDisclosureFg = this.jrDataService.createFormGroupFormattedExplanation();
    this.prepaymentsIntangibleAssetsDisclosureFg = this.jrDataService.createFormGroupFormattedExplanation();
    this.intangibleAssetsOtherDisclosureFg = this.jrDataService.createFormGroupFormattedExplanation();
  }

  patchData(value: JaarrapportageEntity) {
    const immaterieleVasteActiva = value.rapportage?.jaarrekeningen?.at(0)?.balans?.immaterieleVasteActiva;

    if (immaterieleVasteActiva) {
      this.intangibleAssetsFg.reset()

      if (immaterieleVasteActiva.intangibleAssets) {
        this.intangibleAssetsFg.patchValue(immaterieleVasteActiva.intangibleAssets, {emitEvent: false})
      }

      const uitsplitsing = immaterieleVasteActiva.immaterieleVasteActivaUitsplitsing;
      const tekstueel = immaterieleVasteActiva.immaterieleVasteActivaTekstueel;

      if (uitsplitsing) {
        if (uitsplitsing?.costsIncorporationShareIssue) {
          const subItem = uitsplitsing.costsIncorporationShareIssue;
          this.costsIncorporationShareIssueFg.patchValue(subItem)
          this.updateTrialBalanceItems(subItem.trialBalanceItems!, this.costsIncorporationShareIssueFg.get('trialBalanceItems') as FormArray)
        }
        if (uitsplitsing?.developmentCosts) {
          const subItem = uitsplitsing.developmentCosts;
          this.developmentCostsFg.patchValue(subItem)
          this.updateTrialBalanceItems(subItem.trialBalanceItems!, this.developmentCostsFg.get('trialBalanceItems') as FormArray)
        }
        if (uitsplitsing?.goodwill) {
          const subItem = uitsplitsing.goodwill;
          this.goodwillFg.patchValue(subItem)
          this.updateTrialBalanceItems(subItem.trialBalanceItems!, this.goodwillFg.get('trialBalanceItems') as FormArray)
        }
        if (uitsplitsing?.patentsTrademarksOtherRights) {
          const subItem = uitsplitsing.patentsTrademarksOtherRights;
          this.patentsTrademarksOtherRightsFg.patchValue(subItem)
          this.updateTrialBalanceItems(subItem.trialBalanceItems!, this.patentsTrademarksOtherRightsFg.get('trialBalanceItems') as FormArray)
        }
        if (uitsplitsing?.prepaymentsIntangibleAssets) {
          const subItem = uitsplitsing.prepaymentsIntangibleAssets;
          this.prepaymentsIntangibleAssetsFg.patchValue(subItem)
          this.updateTrialBalanceItems(subItem.trialBalanceItems!, this.prepaymentsIntangibleAssetsFg.get('trialBalanceItems') as FormArray)
        }
        if (uitsplitsing?.intangibleAssetsOther) {
          const subItem = uitsplitsing.intangibleAssetsOther;
          this.intangibleAssetsOtherFg.patchValue(subItem)
          this.updateTrialBalanceItems(subItem.trialBalanceItems!, this.intangibleAssetsOtherFg.get('trialBalanceItems') as FormArray)
        }
      }
      if (tekstueel) {
        if (tekstueel.intangibleAssetsDisclosure) {
          this.intangibleAssetsDisclosureFg.patchValue(tekstueel.intangibleAssetsDisclosure);
        }
        if (tekstueel.developmentCostsDisclosure) {
          this.developmentCostsDisclosureFg.patchValue(tekstueel.developmentCostsDisclosure);
        }
        if (tekstueel.patentsTrademarksOtherRightsDisclosure) {
          this.patentsTrademarksOtherRightsDisclosureFg.patchValue(tekstueel.patentsTrademarksOtherRightsDisclosure);
        }
        if (tekstueel.goodwillDisclosure) {
          this.goodwillDisclosureFg.patchValue(tekstueel.goodwillDisclosure);
        }
        if (tekstueel.prepaymentsIntangibleAssetsDisclosure) {
          this.prepaymentsIntangibleAssetsDisclosureFg.patchValue(tekstueel.prepaymentsIntangibleAssetsDisclosure);
        }
        if (tekstueel.intangibleAssetsOtherDisclosure) {
          this.intangibleAssetsOtherDisclosureFg.patchValue(tekstueel.intangibleAssetsOtherDisclosure);
        }
      }
    }
  }

  updateTrialBalanceItems(lijst?: TrialBalanceItem[], formArray?: FormArray) {
    if (lijst && formArray) {
      for (let i = 0; i < lijst.length; i++) {
        if (i < formArray.length) {
          const control = formArray.at(i) as FormGroup;
          control.patchValue(lijst[i], {emitEvent: false});
        } else {
          const formGroup = this.jrDataService.createFormGroupTrialBalanceItem(lijst[i])
          formArray.push(formGroup, {emitEvent: false});
        }
      }
    }
  }

  saveForm(): void {
    this.showCustomControls = false;
    this.cdRef.detectChanges();
    setTimeout(() => {
      this.showCustomControls = true;
      this.cdRef.detectChanges();
    });
    this.costsIncorporationShareIssueFg.updateValueAndValidity({ emitEvent: false });
    this.developmentCostsFg.updateValueAndValidity({ emitEvent: false });
    this.patentsTrademarksOtherRightsFg.updateValueAndValidity({ emitEvent: false });
    this.goodwillFg.updateValueAndValidity({ emitEvent: false });
    this.prepaymentsIntangibleAssetsFg.updateValueAndValidity({ emitEvent: false });
    this.intangibleAssetsOtherFg.updateValueAndValidity({ emitEvent: false });
    this.intangibleAssetsDisclosureFg.updateValueAndValidity({ emitEvent: false });
    this.developmentCostsDisclosureFg.updateValueAndValidity({ emitEvent: false });
    this.patentsTrademarksOtherRightsDisclosureFg.updateValueAndValidity({ emitEvent: false });
    this.goodwillDisclosureFg.updateValueAndValidity({ emitEvent: false });
    this.prepaymentsIntangibleAssetsDisclosureFg.updateValueAndValidity({ emitEvent: false });
    this.intangibleAssetsOtherDisclosureFg.updateValueAndValidity({ emitEvent: false });

    if (this.jrEntity && this.jrEntity.rapportage && this.jrEntity.rapportage.jaarrekeningen) {
      this.jrEntity.rapportage.jaarrekeningen[0] = {
        ...this.jrEntity.rapportage.jaarrekeningen[0],
        balans: {
          ...this.jrEntity.rapportage?.jaarrekeningen[0]?.balans,
          immaterieleVasteActiva: {
            ...this.jrEntity.rapportage?.jaarrekeningen[0]?.balans?.immaterieleVasteActiva,
            immaterieleVasteActivaUitsplitsing: {
              ...this.jrEntity.rapportage?.jaarrekeningen[0]?.balans?.immaterieleVasteActiva?.immaterieleVasteActivaUitsplitsing,
              costsIncorporationShareIssue: this.costsIncorporationShareIssueFg.value,
              developmentCosts: this.developmentCostsFg.value,
              patentsTrademarksOtherRights: this.patentsTrademarksOtherRightsFg.value,
              goodwill: this.goodwillFg.value,
              prepaymentsIntangibleAssets: this.prepaymentsIntangibleAssetsFg.value,
              intangibleAssetsOther: this.intangibleAssetsOtherFg.value,
            },
            immaterieleVasteActivaTekstueel: {
              ...this.jrEntity.rapportage?.jaarrekeningen[0]?.balans?.immaterieleVasteActiva?.immaterieleVasteActivaTekstueel,
              intangibleAssetsDisclosure: this.intangibleAssetsDisclosureFg.value,
              developmentCostsDisclosure: this.developmentCostsDisclosureFg.value,
              patentsTrademarksOtherRightsDisclosure: this.patentsTrademarksOtherRightsDisclosureFg.value,
              goodwillDisclosure: this.goodwillDisclosureFg.value,
              prepaymentsIntangibleAssetsDisclosure: this.prepaymentsIntangibleAssetsDisclosureFg.value,
              intangibleAssetsOtherDisclosure: this.intangibleAssetsOtherDisclosureFg.value
            }
          }
        }
      } as Jaarrekening
    }

    this.jrEntity = {
      ...this.jrEntity,
      rapportage: {
        ...this.jrEntity.rapportage,
        jaarrekeningen: [this.jrEntity.rapportage?.jaarrekeningen?.at(0)]
      }
    } as Jaarrapportage
    this.jrDataService.updateEntity(this.jrEntity);
    this.jrLogicService.getCalculation(this.jrEntity)
  }

}
